import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence, ValueChain } from '@upright/common/model/types';
import { getImpactBarId } from '@upright/test-ids/dom-elements';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { valenceColor } from '@upright/ui/common/generic/utilities/format';
import { LensImpactProfileBar } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/bars/bar';
import { LensGhostTail } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/bars/ghost-tail';
import { InsightBarDataLabel } from '@upright/ui/common/upright/core/impact-lenses/insights/data-area';
import { ImpactBarDataLabel } from '@upright/ui/common/upright/core/impact-lenses/net-impact/data-area/impact-bar-data-labels';
import {
  buildNetImpactDrillInPayload,
  opensImpactDrillIn,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/build-payload';
import { openNetImpactDrillIn } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/state';
import {
  NetImpactLensData,
  NetImpactLensOptions,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { useReleaseFeatureGate } from '@upright/ui/common/upright/model/output/releases/hooks';
import { getValueChainFill } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-chain-fill';
import { color } from 'd3-color';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { NetImpactLensSelectionState } from '../../../../impact-lenses/net-impact/selection-state';
import { useCarouselContext, useCarouselLayout, useImpactBarsLayout } from '../../../context';
import { Bounds } from '../../layout/types';
import { valenceToSide } from '../../utils';
import { PlaceholderSVG } from '../placeholder';

interface Props {
  impact: ImpactCategoryId;
  valence: Valence;
  in?: boolean;
}

export function ImpactBar(props: Props) {
  const baseLayout = useCarouselLayout();
  const context = useCarouselContext();
  const layout = useImpactBarsLayout();
  const lens = context.lens;
  const pushGtmEvent = usePushGtmEvent();
  const allowValueChains = useReleaseFeatureGate('value-chain-splits');

  const state = context.selectionState as NetImpactLensSelectionState;
  const showValueChains = state.showValueChains && allowValueChains;

  const valueChains = showValueChains ? [ValueChain.UPSTREAM, ValueChain.INTERNAL, ValueChain.DOWNSTREAM] : [undefined];
  const barAreas: Array<[ValueChain | undefined, Bounds]> = valueChains.map(valueChain => [
    valueChain,
    layout.impactBarArea(valenceToSide(props.valence), props.impact, valueChain),
  ]);
  function getFill(valueChain?: ValueChain) {
    if (valueChain !== undefined) {
      return getValueChainFill(valueChain);
    }
    if (layout.dimensionLayout) {
      return BouColor.IMPACT_GENERIC_GRAY;
    }
    return valenceColor(props.valence);
  }

  function getHighlightedBorder() {
    if (getFill() === (BouColor.IMPACT_NEGATIVE_PURPLE as string)) {
      return 'black';
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return color(getFill())!.darker(3).formatHex();
  }

  const clickable = lens.isImpactDataAreaClickable
    ? lens.isImpactDataAreaClickable(context.data, props.impact, props.valence)
    : false;

  function handleImpactBarClick() {
    if (!clickable) return;
    pushGtmEvent({
      event: CustomGtmEvent.IMPACT_PROFILE_BAR_CLICK,
      data: {
        impactCode: props.impact,
        valence: props.valence,
        lens: lens.type,
      },
    });

    // PLT-4915: route a bar click on a company net-impact chart into the
    // Net Impact drill-in side panel. Its Chat tab hands off to the
    // existing `ChatSidePanel` on submit — the bar click itself no longer
    // goes straight to chat (which was PLT-4700's design).
    //
    // PLT-5427: the panel now opens for everyone — the per-user variation
    // moved inside the panel (the Chat tab is composer / upsell / hidden
    // per `useNetImpactChatTabMode`). Only the two technical guards
    // remain here: the net-impact lens is the only one with
    // `get_contributing_products` coverage, and only companies — plus the
    // product on the unified product profile, which opts in (PLT-7045) —
    // have a panel body to show (`opensImpactDrillIn`). Groups / portfolios
    // and every other product chart fall through to the default
    // selection-state path (legacy `ComplexImpactDetailsInfoModal`).
    const entity = context.data.entity;
    // Narrow to the net-impact lens-specific options + data shapes —
    // `context` itself is the generic `CarouselContext<ImpactLensType>`,
    // so the casts are only read once the lens type check holds. Same
    // pattern as `use-net-impact-lens-context.ts`.
    const netData = context.data as NetImpactLensData;
    if (lens.type === ImpactLensType.UPRIGHT_NET_IMPACT && entity && opensImpactDrillIn(netData)) {
      const netOptions = context.options as NetImpactLensOptions;
      // Route the click directly to the matching-valence contributors
      // tab. Clicking the negative bar opens on "Negative", clicking
      // the positive bar opens on "Positive" — saves one click vs the
      // v2 design that always landed on a combined overview. (Janne
      // review v3.)
      const initialTab = props.valence === Valence.NEGATIVE ? 'negative' : 'positive';
      openNetImpactDrillIn({
        payload: buildNetImpactDrillInPayload({
          target: { grain: 'impact', impactCode: props.impact },
          entity: { id: entity.id, type: entity.type, name: entity.name },
          scoreReader: netOptions.scoreReader,
          revenue: netData.revenue,
          modelResultId: netData.modelResultId,
          productNames: netData.productNames,
          hasProductViewAccess: netData.hasProductViewAccess,
          // `showValueChains` here is already gate-ANDed but typed boolean|undefined
          // (the selection-state flag is optional); the payload field is strict boolean.
          showValueChains: showValueChains ?? false,
        }),
        initialTab,
      });
      return;
    }

    if (lens.onImpactDataAreaClick) {
      const newState = lens.onImpactDataAreaClick(context.selectionState, props.impact, props.valence);
      context.setSelectionState(newState);
    }
  }

  const isHighlighted =
    lens.isImpactDataAreaHighlighted?.(context.selectionState, props.impact, props.valence) ?? false;
  const border = isHighlighted ? getHighlightedBorder() : 'transparent';
  const testId = getImpactBarId(props.impact, props.valence);

  // Ghost-baseline tail (see `NetImpactLensData.ghostScores`) — undefined for
  // every profile that doesn't set ghost data, i.e. all production profiles.
  const ghostTailArea = layout.impactGhostTailArea(valenceToSide(props.valence), props.impact);

  // JO: added ImpactBarDataLabel and InsightBarDataLabel here as a temporary hack until
  // we can figure out a cleaner way to handle this
  return (
    <CSSTransition
      in={props.in}
      timeout={500}
      key={`${props.impact}+${props.valence}+ImpactBar`}
      classNames={'impactBar'}
      unmountOnExit={true}
    >
      <G onClick={handleImpactBarClick} clickable={clickable} data-testid={testId}>
        {barAreas.map(([valueChain, barArea]) => (
          <Bar
            key={`${props.impact}${props.valence}${valueChain || ''}`}
            bounds={barArea}
            background={getFill(valueChain)}
            border={border}
          />
        ))}
        {ghostTailArea && (
          <LensGhostTail
            bounds={ghostTailArea}
            fill={getFill()}
            patternId={`lens-impact-ghost-${context.data.entity?.id ?? ''}-${props.impact}-${props.valence}`}
          />
        )}
        <ImpactBarDataLabel impact={props.impact} valence={props.valence} />
        <InsightBarDataLabel impact={props.impact} valence={props.valence} />
        <Clickable bounds={baseLayout.impactDataArea(valenceToSide(props.valence), props.impact)} />
      </G>
    </CSSTransition>
  );
}

const Clickable = styled(PlaceholderSVG)`
  opacity: 0;
  pointer-events: fill;
`;

const Bar = styled(LensImpactProfileBar)``;

const G = styled.g<{ clickable: boolean }>`
  ${p =>
    p.clickable
      ? css`
          cursor: pointer;
          &:hover ${Bar} {
            opacity: 0.7;
          }
        `
      : ''};
`;
